export type TranslationVariables = Record<string, string | number>;
export type TranslationValue =
  | string
  | ((variables: TranslationVariables) => string);

export const en = {
  "common.close": "Close",
  "common.cancel": "Cancel",
  "common.back": "Back",
  "common.next": "Next",
  "common.choose": "Choose",
  "common.save": "Save",
  "common.clear": "Clear",
  "common.delete": "Delete",
  "common.view": "View",
  "common.start": "Start",
  "common.stop": "Stop",
  "common.restart": "Restart",
  "common.more": "More",
  "common.later": "Later",
  "common.gotIt": "Got it",
  "common.loading": "Loading...",
  "common.loadMore": "Load more",
  "common.optional": "Optional",
  "common.user": "User",
  "common.all": "All",
  "common.selectAll": "Select all",
  "common.clearAll": "Clear all",
  "common.copied": "Copied",
  "common.copyCommand": "Copy command",
  "common.unknownTime": "Unknown time",
  "common.noWorkspace": "Workspace not set",
  "common.providerRequired": "Configure and enable a provider first",
  "common.providersLoading": "Connecting to the computer...",
  "common.historyReadOnly": "History is read only",
  "common.provider": "Provider",
  "common.agent": "Agent",
  "common.apiKey": "API key",
  "common.enter": "Enter",
  "error.offline": "The local service is offline.",
  "error.notFound": "This item no longer exists.",
  "error.conflict": "This information changed elsewhere. Refresh and try again.",
  "error.permissionDenied": "You do not have permission to perform this action.",
  "error.unauthenticated": "Your Cloud session has expired. Sign in again.",
  "error.invalidArgument": "Some information is invalid. Check it and try again.",
  "error.timeout": "The local service did not respond in time. Try again.",
  "error.unavailable": "This service is temporarily unavailable.",
  "error.unsupported": "This action is not supported here.",
  "nav.home": "Home",
  "nav.sessions": "Sessions",
  "nav.work": "Work",
  "nav.notes": "Idea & Note",
  "nav.api": "API manager",
  "nav.notifications": "Notifications",
  "nav.help": "Help",
  "nav.settings": "Settings",
  "nav.agents": "Agents",
  "nav.skills": "Skills",
  "skills.title": "Skill library",
  "skills.lead": "Browse collected Skills and the commands documented by their source pages.",
  "skills.featured": "Featured",
  "skills.categories": "Browse by category",
  "skills.search": "Search Skills",
  "skills.all": "All Skills",
  "skills.empty": "No Skills are available yet.",
  "skills.unavailable": "The local Skill catalog is unavailable.",
  "skills.loading": "Loading Skill library…",
  "skills.open": "Open Skill",
  "skills.source": "View source",
  "skills.version": "Latest version",
  "skills.copy": "Copy install command",
  "skills.newSessionInstall": "Install in a new session",
  "skills.newSessionUnavailable": "Open a new session to use this command.",
  "skills.count": ({ count }) => `${count} Skills`,
  "skills.noCommand": "No install command was found on the source page.",
  "skills.commandTitle": "Command from the source",
  "skills.commandBody": "Copy the exact command documented on the source page, or use it as the first message in a new session.",
  "nav.language": "Language",
  "nav.themeDark": "Switch to dark mode",
  "nav.themeLight": "Switch to light mode",
  "sync.current": "Synced",
  "sync.pending": "Syncing",
  "sync.error": "Sync error",
  "sync.offline": "Offline",
  "sync.loading": "Checking sync",
  "sync.title": "Synchronization",
  "sync.refresh": "Refresh sync status",
  "sync.pendingDetail": ({ count }) => `${count} item${Number(count) === 1 ? "" : "s"} waiting to sync`,
  "sync.failedDetail": ({ count }) => `${count} failed item${Number(count) === 1 ? "" : "s"} need attention`,
  "language.system": "Use system language",
  "language.systemShort": "System",
  "language.title": "Language",
  "language.description": "Choose the language used by OrniWork. Project content and conversations are never translated.",
  "language.current": "Current language",
  "status.starting": "Starting",
  "status.running": "Running",
  "status.waiting": "Waiting",
  "status.stopping": "Stopping",
  "status.stopped": "Stopped",
  "status.completed": "Completed",
  "status.error": "Error",
  "status.exited": "Exited",
  "status.idle": "Idle",
  "status.blocked": "Blocked",
  "status.planned": "Planned",
  "recovery.interruptionTitle": "Sessions were interrupted",
  "recovery.lead": ({ count }) => `${count} session${Number(count) === 1 ? "" : "s"} were still open when Orni stopped. Review them before continuing.`,
  "recovery.noteTitle": "Process restoration and work continuation are separate",
  "recovery.noteDetail": "Continue sends a private recovery instruction after the Provider is ready. It does not appear as your message and does not repeat completed work.",
  "recovery.group.interrupted_work": "Work may be unfinished",
  "recovery.group.uncertain": "Needs review",
  "recovery.group.idle": "Was open, no unfinished work detected",
  "recovery.evidence.managed_run_waiting": "The work was waiting for input, approval, or a dependency.",
  "recovery.evidence.managed_run_active": "A managed work run was active when the process stopped.",
  "recovery.evidence.tool_started_without_completion": "A tool call started but no completion was recorded.",
  "recovery.evidence.turn_started_without_completion": "The last Provider turn did not record completion.",
  "recovery.evidence.user_message_not_started": "A sent request had not started processing.",
  "recovery.evidence.last_turn_completed": "The most recent Provider turn completed before interruption.",
  "recovery.evidence.provider_ready_without_work": "The Provider was ready, but no active work was recorded.",
  "recovery.evidence.insufficient_provider_events": "There is not enough structured activity to classify this session safely.",
  "recovery.evidence.interrupted_work": "Recent activity suggests work may have been interrupted.",
  "recovery.evidence.uncertain": "The last activity could not be classified safely.",
  "recovery.evidence.idle": "No unfinished Provider work was detected.",
  "recovery.openSession": "Open session",
  "recovery.dismissAll": "Dismiss all",
  "recovery.restoreOnly": "Restore only",
  "recovery.continueSelected": ({ count }) => `Continue selected (${count})`,
  "recovery.error": "The recovery action could not be completed.",
  "profile.native": "Native",
  "profile.orni": "Orni Work",
  "app.documentTitle": "OrniWork · Agent workspace",
  "app.connecting": "Connecting to the local service...",
  "app.palette.aria": "Command palette",
  "app.palette.placeholder": "Search features or sessions...",
  "app.palette.empty": "No matches",
  "app.palette.hint": "Use shortcuts to move quickly. Open sessions and work information from their own lists.",
  "app.product.mobileAria": "Download Orni mobile",
  "app.product.signInAria": "Sign in to Orni",
  "app.product.mobileTitle": "Stay connected to your agents",
  "app.product.signInTitle": "Sign in to Orni",
  "app.product.mobileLead": "Securely connect to sessions running on this computer.",
  "app.product.signInLead": "Sign in for mobile access, remote control, and cross-device sync.",
  "app.product.mobileQrTitle": "Scan to get the mobile app",
  "app.product.mobilePending": "The mobile app is not yet available. Your local sessions are unaffected.",
  "app.product.accountTitle": "Continue with your Orni account",
  "app.product.accountPending": "Secure sign-in will appear here when account services are available.",
  "app.product.localWithoutSignIn": "You can keep using local sessions without signing in.",
  "app.auth.method": "Sign-in method",
  "app.auth.emailCode": "Email code",
  "app.auth.password": "Password",
  "app.auth.forgotPassword": "Forgot password?",
  "app.auth.resetPassword": "Reset your password",
  "app.auth.backToSignIn": "Back to sign in",
  "app.auth.newPassword": "New password",
  "app.auth.confirmPassword": "Confirm password",
  "app.auth.confirmPasswordPlaceholder": "Enter it again",
  "app.auth.passwordMismatch": "The passwords do not match.",
  "app.auth.email": "Email",
  "app.auth.emailPlaceholder": "you@example.com",
  "app.auth.passwordPlaceholder": "At least 10 characters",
  "app.auth.verificationCode": "Verification code",
  "app.auth.codePlaceholder": "6-digit code",
  "app.auth.codeSent": "The code was sent to your email.",
  "app.auth.codeResent": "A new verification code was sent.",
  "app.auth.codeVerified": "Code verified. Choose a new password.",
  "app.auth.resendCode": "Resend code",
  "app.auth.resendIn": ({ seconds }) => `Resend in ${seconds}s`,
  "app.auth.sendCode": "Send code",
  "app.auth.verifyAndSignIn": "Verify and sign in",
  "app.auth.verifyCode": "Verify code",
  "app.auth.passwordSignIn": "Sign in",
  "app.auth.sendResetCode": "Send reset code",
  "app.auth.resetAndSignIn": "Reset password and sign in",
  "app.auth.signingIn": "Signing in",
  "app.auth.unavailable": "Account services are temporarily unavailable.",
  "app.auth.notConfigured": "Set VITE_ORNI_CLOUD_ORIGIN to connect Orni Cloud.",
  "app.auth.signedIn": "Signed in to Orni.",
  "app.auth.signedInTitle": "You are signed in",
  "app.auth.signedInDetail": "Cloud work can now sync across your devices.",
  "app.auth.signOut": "Sign out",
  "app.account.title": "Account security",
  "app.account.lead": "Verify your email before setting a new sign-in password.",
  "app.account.codeDetail": "We will send a code to your verified account email.",
  "app.account.sendCode": "Send verification code",
  "app.account.verifyCode": "Verify code",
  "app.account.codeVerified": "Email verified. Set your new password.",
  "app.account.verifyFirst": "Verify your email before setting a new password.",
  "app.account.currentPassword": "Current password",
  "app.account.currentPasswordPlaceholder": "Leave blank if you have never set one",
  "app.account.currentPasswordDetail": "A current password is required when one is already configured.",
  "app.account.newPassword": "New password",
  "app.account.confirmPassword": "Confirm password",
  "app.account.passwordMismatch": "The passwords do not match.",
  "app.account.passwordUpdated": "Password updated.",
  "app.account.updatePassword": "Update password",
  "app.account.updateFailed": "Could not update the password.",
  "app.auth.browserSignIn": "Continue in your browser",
  "app.auth.browserSignInDetail": "Email, Google, GitHub, and linked account options",
  "app.auth.orSignInHere": "or sign in here",
  "app.auth.callbackWorking": "Finishing sign-in...",
  "app.auth.callbackSuccess": "You are signed in",
  "app.auth.callbackError": "Sign-in could not be completed",
  "app.auth.callbackClose": "You can close this window and return to Orni.",
  "app.recent.title": "Recent sessions",
  "app.recent.viewAll": "View all",
  "app.recent.empty": "No sessions yet",
  "dialog.commandHint": "Run this command in your local terminal. Orni will never install or run it in the background.",
  "home.quickAccess": "Quick access",
  "home.setup": "Setup",
  "home.mobile": "Mobile",
  "home.signIn": "Sign in",
  "home.kicker": "Clear, controlled agent work",
  "home.headline.0": "Interrupted session? Let Orni recover it",
  "home.headline.1": "Start and manage sessions from your phone",
  "home.headline.2": "A dedicated workbench for every session",
  "home.headline.3": "Codex, Claude Code, and OpenCode in one place",
  "home.headline.4": "Keep execution and project knowledge under control",
  "home.headline.5": "Navigate thousands of lines of agent output",
  "home.placeholder": "What do you want to accomplish?",
  "home.providerRequired": "Set up a provider to get started",
  "home.workMode": "Work mode",
  "home.creating": "Creating",
  "home.start": "Start",
  "home.connecting": ({ provider }) => `Creating a session and connecting to ${provider}...`,
  "home.inputTooLong": ({ limit }) => `This message is over ${limit}. Start with the core request, then add longer material in the session.`,
  "home.createFailed": "The session could not be created. Try again.",
  "home.capability.title": "Keep every agent workflow under control",
  "home.capability.lead": "Native terminals, focused chat, and the workbench operate as one connected system. Work keeps running, context stays current, and every change remains traceable.",
  "home.capability.agents.title": "Every agent in one place",
  "home.capability.agents.detail": "Run Codex, Claude Code, and OpenCode together while keeping each native terminal intact.",
  "home.capability.hosting.title": "Work keeps running",
  "home.capability.hosting.detail": "Leave the page without stopping work. Orni records interruptions and restores sessions using your policy.",
  "home.capability.context.title": "One source of truth",
  "home.capability.context.detail": "Goals, requirements, documents, rounds, and progress stay current and guide every agent.",
  "home.capability.trace.title": "Every change is traceable",
  "home.capability.trace.detail": "Questions, decisions, assignments, results, and document changes remain connected.",
  "sessions.title": "Sessions",
  "sessions.lead": "All managed Native and Orni Work sessions.",
  "sessions.filter.running": "Running",
  "sessions.filter.waiting": "Waiting",
  "sessions.filter.completed": "Completed",
  "sessions.search": "Search sessions...",
  "sessions.searchPlaceholder": "Search sessions...",
  "sessions.newSession": "New session",
  "sessions.importSession": "Import session",
  "sessions.switchSession": "Switch session",
  "sessions.emptyTitle": "No sessions yet",
  "sessions.emptyDetail": "Create or import a session to start working.",
  "sessions.noMatches": "No matching sessions",
  "sessions.noMatchesDetail": "Try another search.",
  "sessions.selectTitle": "Choose a session",
  "sessions.selectDetail": "Select a session from the list to open its terminal.",
  "initialization.title": "Finish computer setup",
  "initialization.detail": "Connect an Agent, name this computer, and choose the OrniWork project directory.",
  "initialization.openSettings": "Open setup",
  "initialization.finish": "Start using OrniWork",
  "initialization.previewTitle": "Preview mode",
  "initialization.previewDetail": "This setup surface is isolated in memory. Changes are disabled and the real computer is unchanged.",
  "sessions.deleteSession": "Delete session",
  "sessions.deleteConfirm": ({ title }) => `Delete “${title}”? Session history will be removed.`,
  "sessions.column.session": "Session",
  "sessions.column.status": "Status",
  "sessions.column.progress": "Progress",
  "sessions.column.updated": "Updated",
  "sessions.runCount": ({ count }) => `${count} run${Number(count) === 1 ? "" : "s"}`,
  "sessions.deletePrompt": "Delete this session?",
  "sessions.stopTitle": "Stop session?",
  "sessions.restartTitle": "Restart session?",
  "sessions.stopDetail": "The provider process will stop. Session history will be preserved.",
  "sessions.restartDetail": "The provider process will restart and any running operation may be interrupted.",
  "sessions.stopAction": "Stop session",
  "sessions.restartAction": "Restart session",
  "sessions.stopAll": "Stop all",
  "sessions.stopAllTitle": "Stop all sessions?",
  "sessions.stopAllDetail": "Every running coordinator and worker Provider process will stop. Session history and Work information will be preserved.",
  "sessions.stopAllAction": "Stop all sessions",
  "sessions.computer": "Computer",
  "sessions.chooseComputer": "Choose computer",
  "sessions.computerTargetHint": "New sessions run on this computer. The list still shows every computer.",
  "sessions.computerUnavailable": "No connected computer was returned yet. Try again shortly.",
  "sessions.allComputers": "All computers",
  "sessions.computerScope": "New computer-scoped actions use this computer",
  "sessions.fleetScope": "Session list stays across all computers",
  "notifications.title": "Notifications",
  "notifications.lead": "Questions, reviews, and other items that need your attention appear here.",
  "notifications.pending": "Pending",
  "notifications.emptyTitle": "You're all caught up",
  "notifications.emptyDetail": "New items that need your attention will appear here.",
  "work.title": "Work",
  "work.workspaceTitle": "Workspace",
  "work.lead": "Track goals, plans, todos, progress, and confirmations across sessions.",
  "work.redesignDetail": "The Orni Work workspace is being redesigned. Your Native sessions remain available from Sessions.",
  "work.redesignStatus": "Work data is temporarily paused",
  "work.filter.all": "All work",
  "work.filter.confirm": "Needs confirmation",
  "work.filter.running": "Running",
  "work.filter.blocked": "Blocked",
  "work.filter.planned": "Planned",
  "work.filter.completed": "Completed",
  "work.filter.status": "Status",
  "work.filter.sessionType": "Session type",
  "work.filter.allSessions": "All sessions",
  "work.empty.pending": "No pending items in this view",
  "work.section.completed": "Completed work",
  "work.section.planned": "Planned work",
  "work.section.active": "Active sessions",
  "work.empty.sessions": "No matching sessions in this view",
  "work.section.todos": "Plans and todos",
  "work.approve": "Approve",
  "work.reject": "Reject",
  "work.openSession": "Open session",
  "createMenu.newSession": "New session",
  "createMenu.nativeOnlyHint": "OpenCode currently supports Native sessions only",
  "createMenu.nativeOnly": "Native only",
  "createMenu.import": "Import existing project",
  "create.mode.nativeDetail": "Keep the original CLI terminal and interaction",
  "create.mode.orniDetail": "Maintain goals, plans, context, and progress as needed",
  "create.permission.readOnly": "Read only",
  "create.permission.readOnlyDetail": "View and analyze without changing files",
  "create.permission.workspaceWrite": "Workspace write",
  "create.permission.workspaceWriteDetail": "Allow changes in the current workspace",
  "create.permission.fullAccess": "Full access",
  "create.permission.fullAccessDetail": "Allow file and command operations",
  "create.unknownWorkspace": "Unknown workspace",
  "create.importedProject": "Imported project",
  "create.title.native": ({ provider }) => `New ${provider} Native Session`,
  "create.title.orni": ({ provider }) => `New ${provider} Orni Work Session`,
  "create.nativeLead": "Start a fresh native Agent session",
  "create.historyError": "No session history was found. Check the project directory.",
  "create.historyDiscoverError": "Could not read the selected CLI session library.",
  "create.import.title": "Import existing project",
  "create.import.sessionsTitle": "Import CLI sessions",
  "create.import.sessionsLead": "Choose Codex, Claude Code, or OpenCode, then select its existing sessions.",
  "create.import.sourcesTitle": "Choose information to import",
  "create.import.detailsLead": "Choose the project directory and AI, then select the information to reference.",
  "create.import.sourcesLead": "Choose the session history and project content OrniWork should reference.",
  "create.projectName": "Project name",
  "create.projectNameAuto": "Filled from the selected directory",
  "create.sessionName": "Session name",
  "create.sessionNamePlaceholder": "Session name, e.g. Payment module refactor",
  "create.initialInstruction": "First instruction",
  "create.initialInstructionPlaceholder": "Optional: start the new session with an instruction",
  "create.sessionType": "Session type",
  "create.projectDirectory": "Project directory",
  "create.projectDirectoryAria": "Project directory to import",
  "create.chooseDirectory": "Choose directory",
  "create.advanced": "Advanced settings",
  "create.autoGenerated": "Generated automatically",
  "create.workspace": "Workspace",
  "create.provider": "Provider",
  "create.workspacePlaceholder": "Generated automatically, or enter or choose a directory",
  "create.workspaceAria": "Workspace; leave empty to generate automatically",
  "create.chooseWorkspace": "Choose workspace",
  "create.permissions": "Permissions",
  "create.scope.aria": "Project information scope",
  "create.scope.documents": "Documents",
  "create.scope.documentsDetail": "Requirements, notes, and guidelines",
  "create.scope.code": "Code",
  "create.scope.codeDetail": "Implementation, configuration, and tests",
  "create.scope.gitDetail": "Status, commits, and history",
  "create.scope.other": "Other",
  "create.scope.otherDetail": "Other related project content",
  "create.history.title": "Session history",
  "create.history.filter": "Imported session filter",
  "create.history.notImported": "Not imported",
  "create.history.imported": "Imported",
  "create.history.noImported": "No imported sessions",
  "create.history.refresh": "Refresh CLI sessions",
  "create.history.selected": ({ selected, total }) => `Selected ${selected} of ${total}`,
  "create.history.emptyTitle": "No matching session history found",
  "create.history.emptyDetail": "You can still start from the selected documents, code, and Git content.",
  "create.history.close": "Close history",
  "create.history.emptyMessages": "No history messages to display",
  "create.history.loading": "Finding session history...",
  "create.history.sessionCount": ({ count }) => `${count} session${Number(count) === 1 ? "" : "s"}`,
  "create.history.deselect": "Deselect session",
  "create.history.select": "Select session",
  "create.history.noPreview": "No user message to display",
  "create.history.messageCount": ({ count }) => `${count} message${Number(count) === 1 ? "" : "s"}`,
  "create.creating": "Creating...",
  "create.importAndOpen": "Import and open",
  "create.createAndOpen": "Create and open",
  "platform.update.aria": "OrniWork update",
  "platform.update.required": "Update required",
  "platform.update.available": "New version available",
  "platform.update.fallback": "Update to continue using the latest provider and session management features.",
  "platform.update.action": "Update now",
  "platform.announcement.aria": "Announcement",
  "platform.announcement.label": "OrniWork announcement",
  "platform.announcement.details": "View details",
  "settings.title": "Settings",
  "settings.lead": "Manage providers, project directories, and the local runtime.",
  "agents.title": "Agent management",
  "agents.lead": "Manage the agent CLIs available on this computer and import their existing sessions.",
  "agents.providers.title": "Installed agents",
  "agents.providers.lead": "Enable, configure, and inspect the agent CLIs used by new sessions.",
  "agents.providers.candidate": "Candidate adapter · discovery only",
  "agents.providers.candidateStatus": "Not available for runtime yet",
  "agents.history.open": "Import history",
  "settings.agents.title": "Agent management",
  "settings.agents.lead": "Agent CLIs now have their own management surface.",
  "settings.agents.actionTitle": "Manage installed agents",
  "settings.agents.actionDetail": "Enable tools, configure paths, and import native sessions from one place.",
  "settings.agents.open": "Open Agent management",
  "settings.transport.title": "Session connection",
  "settings.transport.lead": "Choose where this Desktop sends session commands.",
  "settings.transport.local.title": "This computer",
  "settings.transport.local.detail": "Create and control sessions directly through the local Daemon.",
  "settings.transport.local.action": "Local",
  "settings.transport.remote.title": "Remote computers",
  "settings.transport.remote.detail": "Use Orni Cloud to create and control sessions on another signed-in computer.",
  "settings.transport.remote.action": "Remote",
  "settings.runSetup": "Run setup again",
  "settings.appearance.title": "Appearance and language",
  "settings.appearance.lead": "Changes apply immediately and are kept for your next launch.",
  "settings.theme.title": "Theme",
  "settings.theme.detail": "Native terminals keep each provider's own color scheme.",
  "settings.theme.dark": "Dark",
  "settings.theme.light": "Light",
  "settings.language.title": "Language",
  "settings.language.detail": "System language is used until you choose another.",
  "settings.provider.lead": "These settings apply to newly started and recovered sessions.",
  "settings.provider.refresh": "Check again",
  "settings.provider.loading": "Loading providers...",
  "settings.provider.emptyInventory": "No Agents configured",
  "settings.provider.emptyInventoryDetail": "Add or enable an Agent to make it available for new sessions.",
  "settings.projectRoot.title": "OrniWork project directory",
  "settings.projectRoot.lead": "New sessions create isolated workspaces under this directory by default.",
  "settings.access.title": "Remote access boundaries",
  "settings.access.lead": "Limit what Cloud and mobile clients can access on this computer.",
  "settings.access.directory.title": "Permitted directory",
  "settings.access.directory.detail": "Remote directory browsing and session workspaces must stay within this root. Local Desktop actions are unaffected.",
  "settings.defaultPermission.title": "Default session permission",
  "settings.defaultPermission.detail": "Used for every new session unless you change it before creation.",
  "settings.computerName.title": "Computer name",
  "settings.computerName.detail": "Shown when this computer is selected from Cloud or mobile. Changes are saved when you leave the field.",
  "settings.computerName.placeholder": "Name this computer",
  "settings.access.saved": "Access settings saved",
  "settings.access.saveFailed": "Could not save access settings",
  "settings.service.title": "Local service",
  "settings.service.lead": "The daemon manages provider lifecycles, PTY history, and failure recovery.",
  "settings.service.detail": "All provider processes are managed by one local service.",
  "settings.service.online": "Online",
  "settings.service.offline": "Offline",
  "settings.credentials.title": "Credential protection",
  "settings.credentials.detail": "API keys remain in local configuration and never appear in the UI, workbench, or logs.",
  "settings.credentials.local": "Local",
  "settings.command.install": "Install",
  "settings.command.upgrade": "Upgrade",
  "settings.command.uninstall": "Uninstall",
  "settings.provider.uninstallConfirm": "Uninstall this Agent from the computer?",
  "settings.provider.installConfirm": "Install this Agent on the computer?",
  "settings.provider.upgradeConfirm": "Upgrade this Agent on the computer?",
  "settings.agentReceipt.accepted": "Operation accepted",
  "settings.agentReceipt.running": "Operation in progress",
  "settings.agentReceipt.succeeded": "Operation completed",
  "settings.agentReceipt.failed": "Operation failed",
  "settings.agentReceipt.rejected": "Operation rejected",
  "settings.agentReceipt.cancelObserve": "Stop waiting",
  "settings.agentReceipt.resumeObserve": "Continue watching",
  "settings.agentReceipt.retry": "Retry",
  "settings.command.detail": "OrniWork will not run this command. Review it before copying it to your local terminal.",
  "settings.provider.updated": "Provider status updated",
  "settings.provider.checkFailed": "Provider check failed",
  "settings.provider.saveFailed": "Could not save provider settings",
  "settings.provider.installed": "Installed",
  "settings.provider.notDetected": "Installation not detected",
  "settings.provider.enabled": "Enabled",
  "settings.provider.disabled": "Disabled",
  "settings.provider.notInstalled": "Not installed",
  "settings.provider.enable": "Enable provider",
  "settings.provider.disable": "Disable provider",
  "settings.provider.enableConfirm": "Enable this Agent on the computer?",
  "settings.provider.disableConfirm": "Disable this Agent? Existing sessions will not be stopped.",
  "settings.provider.executable": "Executable",
  "settings.provider.executablePlaceholder": "Enter the executable path",
  "settings.provider.home": "Home directory",
  "settings.provider.manageApi": "Manage API",
  "settings.provider.defaultModel": "Default model",
  "settings.provider.defaultModelPlaceholder": "Use the provider default",
  "settings.provider.endpoint": "API endpoint",
  "settings.provider.keyConfigured": "Configured; leave blank to keep unchanged",
  "onboarding.detectFailed": "Provider detection failed",
  "onboarding.providerRequired": "Enable at least one installed provider with a valid path.",
  "onboarding.providerSaveFailed": "Could not save provider settings",
  "onboarding.historyReadFailed": "Could not read session history",
  "onboarding.settingsSaveFailed": "Setup could not be saved",
  "onboarding.progress": "Setup progress",
  "onboarding.step.providers": "Connect agents",
  "onboarding.step.history": "Import history",
  "onboarding.close": "Close setup",
  "onboarding.providers.eyebrow": "First-time setup · Local tools",
  "onboarding.providers.title": "Connect the agents you already use",
  "onboarding.providers.lead": "Choose your terminal agents. OrniWork connects, records, and organizes them without changing their native sessions.",
  "onboarding.providers.refresh": "Check again",
  "onboarding.providers.installed": "Installed",
  "onboarding.providers.notInstalled": "Not installed",
  "onboarding.providers.enable": ({ provider }) => `Enable ${provider}`,
  "onboarding.providers.executable": "Executable",
  "onboarding.providers.executablePlaceholder": "Enter the executable path",
  "onboarding.providers.home": "Home directory",
  "onboarding.providers.defaultModel": "Default model",
  "onboarding.providers.defaultModelPlaceholder": "Use the provider default",
  "onboarding.providers.endpoint": "API endpoint",
  "onboarding.providers.keyConfigured": "Configured; leave blank to keep it",
  "onboarding.providers.copyInstall": "Copy install command",
  "onboarding.providers.checking": "Checking local tools...",
  "onboarding.providers.checkingShort": "Checking providers",
  "onboarding.providers.checkFailed": "Provider check failed",
  "onboarding.providers.checkFailedShort": "Unavailable",
  "onboarding.providers.checkFailedDetail": "The target computer did not return its installed tools. Check the connection and try again.",
  "onboarding.providers.retry": "Retry",
  "onboarding.providers.emptyInventory": "No Provider information was received. Check that the selected computer is online, then try again.",
  "onboarding.providers.managedCount": ({ count }) => `${count} tool${Number(count) === 1 ? "" : "s"} will be managed by OrniWork`,
  "onboarding.providers.managedLabel": "tools connected to OrniWork",
  "onboarding.providers.localOnly": "Settings stay on this computer",
  "onboarding.providers.historyStays": "Native history is never moved",
  "onboarding.providers.rerun": "Run setup again from Settings at any time",
  "onboarding.providers.foot": "Enable one installed tool to continue. Paths and model preferences can be adjusted later.",
  "onboarding.history.eyebrow": "Step two · Optional import",
  "onboarding.history.title": "Bring your existing sessions into view",
  "onboarding.history.lead": "Review session summaries grouped by provider and workspace. You can finish now and import projects later.",
  "onboarding.history.discovered": "Sessions found",
  "onboarding.history.existing": "Existing sessions",
  "onboarding.history.selected": ({ selected, total }) => `${selected} of ${total} selected. Bulk selections can still be adjusted individually.`,
  "onboarding.history.safeTitle": "Your existing sessions stay intact",
  "onboarding.history.scanDone": "Scan complete.",
  "onboarding.history.scanRunning": "Scanning continues in the background and will not block setup.",
  "onboarding.history.safeDetail": "You can browse and import history by directory later.",
  "onboarding.footer.enabled": ({ count }) => `${count} provider${Number(count) === 1 ? "" : "s"} enabled`,
  "onboarding.footer.saving": "Saving...",
  "onboarding.footer.continue": "Continue",
  "onboarding.footer.finish": "Finish setup",
  "onboarding.history.scopeLabel": ({ name }) => `Sessions under ${name}`,
  "onboarding.history.providerLabel": ({ provider }) => `${provider} sessions`,
  "onboarding.history.noSummary": "No summary available",
  "onboarding.history.readingTitle": "Reading session history",
  "onboarding.history.readingDetail": "You can keep using other settings while this runs.",
  "onboarding.history.errorTitle": "History is temporarily unavailable",
  "onboarding.history.errorFallback": "Could not read history. Try again.",
  "onboarding.history.retry": "Try again",
  "onboarding.history.emptyTitle": "No complete sessions available to import",
  "onboarding.history.emptyDetail": "You can still create sessions and import again later.",
  "onboarding.history.selectScope": ({ label }) => `Select ${label}`,
  "onboarding.history.clearScope": ({ label }) => `Clear ${label}`,
  "onboarding.history.select": "Select all",
  "onboarding.history.clear": "Clear",
  "onboarding.history.timeout": "There is a lot of history and the scan timed out. Try again; other agents are unaffected.",
  "terminal.quick.continue": "Continue current work",
  "terminal.quick.fix": "Fix this issue",
  "terminal.quick.analyze": "Analyze before changing",
  "terminal.quick.alternatives": "Is there a better approach?",
  "terminal.quick.test": "Run tests and fix failures",
  "terminal.quick.review": "Review current changes",
  "terminal.quick.summary": "Summarize progress and next steps",
  "terminal.inputFailed": "Could not send terminal input",
  "terminal.unavailable": "The local terminal connection is unavailable",
  "terminal.oldRun": "This input belongs to an earlier run. Its native terminal history is not available in the current run.",
  "terminal.promptFailed": "Could not send the confirmation",
  "terminal.aria": "Native terminal",
  "terminal.userInputs": "User input history",
  "terminal.outputResume": "Resume output",
  "terminal.outputPause": "Pause output",
  "terminal.follow": "Follow latest output",
  "terminal.online": "Terminal online",
  "terminal.reconnect": "Reconnect",
  "terminal.connecting": "Waiting for the native terminal...",
  "terminal.snapshotTimeout": "The terminal screen did not arrive in time. Reconnect to try again.",
  "terminal.controlAcquiring": "Acquiring terminal control...",
  "terminal.controlAcquired": "Terminal control acquired",
  "terminal.controlHeldElsewhere": "Another device currently controls this terminal.",
  "terminal.controlTakeover": "No input control · Get control",
  "terminal.disconnected": "Terminal connection interrupted. Reconnecting automatically.",
  "terminal.reconnectNow": "Reconnect now",
  "terminal.quick.title": "Shortcuts",
  "terminal.copySelection": "Copy selection",
  "terminal.up": "Up",
  "terminal.down": "Down",
  "terminal.keysCollapse": "Hide terminal keys",
  "terminal.keysShow": "Show terminal keys",
  "terminal.historyLoading": "Loading terminal history...",
  "terminal.attachImage": "Attach image to terminal",
  "terminal.imageUploading": "Uploading image",
  "terminal.imageReady": "Image path inserted",
  "terminal.removeImage": "Remove image",
  "terminal.imageTypeUnsupported": "Use a PNG, JPEG, or WebP image.",
  "terminal.imageTooLarge": "Images must be smaller than 10 MB.",
  "terminal.imageUnavailable": "Image upload is unavailable for this terminal.",
  "recovery.saveFailed": "Could not save recovery settings",
  "recovery.enableTitle": "Automatic recovery",
  "recovery.enableDetail": "Recover from temporary capacity limits, network errors, retryable failures, or unexpected provider exits.",
  "recovery.window": "Recovery window",
  "recovery.interval": "Retry interval",
  "recovery.minute": ({ count }) => `${count} minute${Number(count) === 1 ? "" : "s"}`,
  "recovery.second": ({ count }) => `${count} seconds`,
  "recovery.foreverTitle": "Keep trying",
  "recovery.foreverDetail": "Ignore the recovery window until the session recovers, recovery is disabled, or the session is explicitly stopped.",
  "recovery.attemptsUsed": ({ count }) => `${count} recovery attempt${Number(count) === 1 ? "" : "s"} in this incident`,
  "recovery.saving": "Saving...",
  "recovery.save": "Save settings",
  "recovery.saved": "Settings saved",
  "recovery.noTriggerHint": "Questions, permission prompts, and user stops never trigger recovery.",
  "recovery.settingsEyebrow": "Settings",
  "recovery.title": "Failure recovery",
  "recovery.pageLead": "Protect the coordinator and worker sessions in this Work, restoring interrupted work after temporary failures or unexpected exits.",
  "recovery.allEnabled": "Enabled for all",
  "recovery.partlyEnabled": "Partly enabled",
  "recovery.policyTitle": "Recovery policy",
  "recovery.policyLead": "Changes apply to the coordinator and every worker in this Work. New workers inherit recovery by default.",
  "recovery.sessionsTitle": "Protected sessions",
  "recovery.sessionsLead": "Coordinator and worker sessions share the same recovery boundary. Waiting for input and normal stops never restart.",
  "recovery.sessionCount": ({ count }) => `${count} session${Number(count) === 1 ? "" : "s"}`,
  "recovery.coordinator": "Coordinator",
  "recovery.enabled": "Enabled",
  "recovery.disabled": "Disabled",
  "recovery.recovering": ({ count }) => `Recovering · attempt ${count}`,
  "recovery.historyTitle": "Recovery history",
  "recovery.historyLead": "Only actual failures and recovery results appear here, not normal waiting, user input, or routine progress.",
  "recovery.recordCount": ({ count }) => `${count} record${Number(count) === 1 ? "" : "s"}`,
  "recovery.historyEmpty": "No recovery has been triggered",
  "recovery.dialogLead": "Resume the current request after an unexpected interruption. Normal completion and waiting for your reply never continue automatically.",
  "recovery.dialogAria": "Failure recovery settings",
  "recovery.outcome.scheduled": "Waiting to recover",
  "recovery.outcome.dispatched": "Recovery request sent",
  "recovery.outcome.succeeded": "Recovered",
  "recovery.outcome.cancelled": "Cancelled",
  "recovery.outcome.failed": "Recovery failed",
  "recovery.attemptNumber": ({ count }) => `Attempt ${count}`,
  "recovery.issue.capacity": "Model capacity temporarily unavailable",
  "recovery.issue.rateLimit": "Request rate temporarily limited",
  "recovery.issue.timeout": "Provider connection timed out",
  "recovery.issue.processExit": "Provider process exited unexpectedly",
  "recovery.issue.fallback": "Temporary provider failure",
  "help.kicker": "OrniWork Help",
  "help.title": "Keep CLI agents working without losing control",
  "help.lead": "OrniWork manages AI coding sessions on your computer, so work can continue without constant terminal supervision or lost context.",
  "help.free": "Core desktop features are free",
  "help.problem.interruptedTitle": "A session stops unexpectedly",
  "help.problem.interruptedDetail": "Detect, retry, and restore the work",
  "help.problem.remoteTitle": "You have to stay at your desk",
  "help.problem.remoteDetail": "Mobile control arrives with remote access",
  "help.problem.complexTitle": "Complex work becomes hard to follow",
  "help.problem.complexDetail": "Keep requirements, plans, and progress current",
  "help.capability.eyebrow": "Core capabilities",
  "help.capability.title": "Let OrniWork handle the continuity",
  "help.capability.lead": "Keep using the CLI you know. OrniWork manages the work that usually falls outside the conversation.",
  "help.capability.0.title": "Sessions keep running when you step away",
  "help.capability.0.detail": "OrniWork hosts sessions from Codex, Claude Code, OpenCode, and other local CLI tools. It detects retryable interruptions and keeps sessions running after the desktop window closes.",
  "help.capability.0.feature0": "Automatic recovery after interruptions",
  "help.capability.0.feature1": "Alerts for stalls and long silence",
  "help.capability.0.feature2": "Recovery state and attempt history",
  "help.capability.1.title": "Mobile and remote access",
  "help.capability.1.detail": "Viewing and replying to sessions from mobile requires the upcoming remote service. The current release focuses on local desktop sessions and workbench management.",
  "help.capability.1.feature0": "Planned: live session output",
  "help.capability.1.feature1": "Planned: answer confirmations and choices",
  "help.capability.1.feature2": "Planned: send messages through the active session",
  "help.capability.2.title": "Find old sessions without guesswork",
  "help.capability.2.detail": "No need to remember directories or inspect unnamed history. OrniWork discovers complete local sessions by workspace across providers.",
  "help.capability.2.feature0": "Consistent naming and sorting",
  "help.capability.2.feature1": "Local imports across providers",
  "help.capability.2.feature2": "Continue directly from the session list",
  "help.capability.3.title": "The native CLI stays intact",
  "help.capability.3.detail": "Terminal styling, slash commands, keyboard workflows, and provider-native features remain available whenever you need full detail.",
  "help.capability.3.feature0": "Complete native terminal",
  "help.capability.3.feature1": "Provider commands remain available",
  "help.capability.3.feature2": "Direct desktop control",
  "help.capability.4.title": "Important context survives",
  "help.capability.4.detail": "OrniWork maintains work facts, requirement changes, and working context so the right information can be restored after compaction, interruption, or a handoff.",
  "help.capability.4.feature0": "Context recovery and reinjection",
  "help.capability.4.feature1": "Requirement and decision history",
  "help.capability.4.feature2": "Continuous work across sessions",
  "help.capability.5.title": "Complex work stays understandable",
  "help.capability.5.detail": "Requirements, plans, todos, progress, and confirmations stay organized. Large efforts can be split into smaller units while preserving dependencies and outcomes.",
  "help.capability.5.feature0": "Requirements, plans, and todos",
  "help.capability.5.feature1": "Progress and confirmations in one place",
  "help.capability.5.feature2": "Decomposition and collaboration",
  "help.compare.eyebrow": "Compared with Native alone",
  "help.compare.title": "The same agent, with a continuity layer",
  "help.compare.native": "Native only",
  "help.compare.orni": "With OrniWork",
  "help.compare.0.native": "Sessions scattered across workspaces and providers",
  "help.compare.0.orni": "Discover, import, and switch by workspace",
  "help.compare.1.native": "Manually search long output for progress and decisions",
  "help.compare.1.orni": "Progress, confirmations, documents, and changes stay structured",
  "help.compare.2.native": "Recover manually and explain context again",
  "help.compare.2.orni": "Recover sessions and reinject authoritative work information",
  "help.enhance.eyebrow": "Session controls",
  "help.enhance.title": "Keep the native CLI, make sessions easier to use",
  "help.enhance.lead": "Every session gets these viewing and control options. Orni Work also maintains work documents when useful.",
  "help.enhance.0.title": "Native terminal and focused chat",
  "help.enhance.0.detail": "Switch between the complete CLI and a conversation view that focuses on user input, meaningful progress, and conclusions.",
  "help.enhance.1.title": "Input history and exact navigation",
  "help.enhance.1.detail": "Review commands by time and jump from any entry to the matching point in the terminal or focused chat.",
  "help.enhance.2.title": "Sessions across providers",
  "help.enhance.2.detail": "Codex, Claude Code, and OpenCode appear together. Complete history imports by workspace instead of creating a separate workflow.",
  "help.enhance.3.title": "Follow live output or inspect history",
  "help.enhance.3.detail": "Follow new output automatically, keep your position while reviewing history, and return to the latest activity in one step.",
  "help.enhance.4.title": "Input follows the active session",
  "help.enhance.4.detail": "The terminal and focused chat share one session. Type or choose an answer where you are, and Orni routes it to that session automatically.",
  "help.enhance.5.title": "Expand long replies only when needed",
  "help.enhance.5.detail": "Focused chat collapses very long replies by default. Expand one message or temporarily show everything to reduce scrolling noise.",
  "help.services.eyebrow": "Tools and services",
  "help.services.title": "Manage setup and usage in one place",
  "help.services.0.title": "CLI tool management",
  "help.services.0.detail": "Install, check, and upgrade Codex, Claude Code, OpenCode, and other providers.",
  "help.services.1.title": "Token usage",
  "help.services.1.detail": "Review token use by session and provider before budgets drift.",
  "help.services.2.title": "APIs and routing",
  "help.services.2.detail": "Configure local CLIs, API routing, and model switching from one place when external model APIs are needed.",
  "help.services.3.title": "Local first",
  "help.services.3.detail": "Sessions and core features run on your computer. Local use does not require an OrniWork account.",
  "help.modes.eyebrow": "Session modes",
  "help.modes.title": "Start simple and add structure when needed",
  "help.modes.native": "Keep the complete native terminal. OrniWork provides hosting, history indexing, and read-only observation without changing how you work.",
  "help.modes.orni": "Maintain goals, requirements, plans, todos, context, progress, and changes when the work needs them. A simple task can grow into a deeper project over time.",
  "help.faq.eyebrow": "FAQ",
  "help.faq.title": "What to know before you start",
  "help.faq.0.q": "Does OrniWork replace Codex or Claude Code?",
  "help.faq.0.a": "No. OrniWork manages the CLI tools already installed on your computer. Native sessions and provider features remain available; Orni adds local hosting and work information management.",
  "help.faq.1.q": "Can I control sessions from mobile after the computer is off?",
  "help.faq.1.a": "No. Full mobile remote control is not available yet, and future remote access will still require the host computer to be powered on and online.",
  "help.faq.2.q": "Does the desktop app require payment or sign-in?",
  "help.faq.2.a": "Core desktop features are free and local use does not require sign-in. Remote access, multi-device sync, model APIs, and some network services may require an account or separate purchase.",
  "help.faq.3.q": "Does OrniWork upload my full codebase and conversations?",
  "help.faq.3.a": "Local terminals and sessions are managed on your computer. Data is transferred only when you enable a remote or network service, and the requested scope will be shown before activation.",
  "help.faq.4.q": "Which AI coding tools are supported?",
  "help.faq.4.a": "The architecture supports Codex, Claude Code, OpenCode, and future providers. Each provider adapter handles its own structured events, recovery behavior, and advanced capabilities.",
  "help.faq.5.q": "What does switching API routes without restarting mean?",
  "help.faq.5.a": "With Orni's local API configuration and routing enabled, you can change model APIs and routing policy from one place without restarting each CLI. This network service remains separate from the free local core.",
  "session.back": "Back",
  "session.nameSave": "Save name",
  "session.nameCancel": "Cancel rename",
  "session.rename": "Rename session",
  "session.executionReadonly": "Worker session · Read only",
  "session.native": "Native session",
  "session.recovery": "Failure recovery",
  "session.stop": "Stop session",
  "session.startConnect": "Start and connect",
  "session.restart": "Restart session",
  "session.localCommandShow": "Show local command",
  "session.more": "More",
  "session.upgrade.title": "Upgrade to Orni Work",
  "session.upgrade.menuDetail": "Keep this session and start maintaining a workbench",
  "session.organize.title": "Refresh workbench",
  "session.organize.detail": "Ask the coordinator to review existing information",
  "session.stopConfirm": "Stop session?",
  "session.restartConfirm": "Restart session?",
  "session.stopDetail": "The provider process will stop. Saved session history will be preserved.",
  "session.restartDetail": "The provider process will restart and any running operation may be interrupted.",
  "session.localCommand.title": "Open this session locally",
  "session.localCommand.detail": "Run this command in a local terminal to enter the same managed session.",
  "session.directory.view": "View current directory",
  "session.directory.viewDetail": "The working directory used by this Session.",
  "session.directory.new": "New Session (Current Dir)",
  "session.directory.newDetail": "Choose a Provider to open a new native Session in this directory.",
  "session.upgrade.lead": "Keep the current session and make it the coordinator responsible for the workbench and work assignments.",
  "session.upgrade.current": "Current session",
  "session.upgrade.currentDetail": "Included automatically as existing context",
  "session.upgrade.sources": "Let the coordinator reference",
  "session.upgrade.document": "Documents",
  "session.upgrade.code": "Code",
  "session.upgrade.other": "Other",
  "session.upgrade.pending": "Upgrading...",
  "session.upgrade.confirm": "Upgrade",
  "session.starting.createTitle": "Creating session",
  "session.starting.providerTitle": ({ provider }) => `Starting ${provider}`,
  "session.starting.createDetail": "Preparing the workspace and connecting to the provider...",
  "session.starting.providerDetail": "The native terminal appears automatically when the provider is ready.",
  "session.starting.initialMessage": "Your message",
  "session.inactive.error": ({ provider }) => `${provider} connection failed`,
  "session.inactive.exited": ({ provider }) => `${provider} exited`,
  "session.inactive.title": "Session not started",
  "session.inactive.workerDetail": "This worker session is not running. Return to the coordinator for its work assignment.",
  "session.inactive.detail": ({ provider }) => `Start to launch and connect the ${provider} native terminal.`,
  "session.simple.you": "You",
  "session.simple.sending": "Sending...",
  "session.simple.sent": "Sent",
  "session.simple.failed": "Send failed",
  "session.simple.retry": "Send again",
  "session.simple.collapse": "Collapse",
  "session.simple.expand": "Expand",
  "session.simple.copy": "Copy",
  "session.simple.empty": "Send a message to start the focused conversation view.",
  "session.simple.recovery.capacity": "Model capacity is limited. Recovering",
  "session.simple.recovery.rateLimit": "Requests are temporarily limited. Recovering",
  "session.simple.recovery.timeout": "Connection timed out. Recovering",
  "session.simple.recovery.unavailable": "Service unavailable. Recovering",
  "session.simple.recoveryAttempt": ({ count }) => `attempt ${count}`,
  "session.simple.turnFailed": "This turn failed. Send it again or adjust failure recovery settings.",
  "session.simple.follow": "Follow latest output",
  "session.simple.expandLong": "Expand long messages",
  "session.simple.collapseLong": "Collapse long messages",
  "session.simple.toolActivity": "Tool activity",
  "session.simple.toolRunning": "Running",
  "session.simple.toolCompleted": "Completed",
  "session.simple.toolFailed": "Failed",
  "session.simple.toolInput": "Input",
  "session.simple.toolOutput": "Output",
  "session.simple.toolError": "Error",
  "session.simple.toolExitCode": "Exit code",
  "session.simple.turnActive": "Agent is working",
  "session.simple.queueTitle": "Waiting for Agent acceptance",
  "session.simple.queueQueued": "Queued locally",
  "session.simple.queueRelayAccepted": "Accepted by Cloud relay",
  "session.simple.queueDaemonAccepted": "Accepted by the computer",
  "session.simple.queueProviderAccepted": "Accepted by the Agent",
  "session.simple.queueIndeterminate": "Delivery status is being reconciled",
  "session.simple.queueCancelled": "Cancelled",
  "session.composer.readonly": "Worker sessions are read only. Continue in the coordinator session.",
  "session.composer.placeholder": "Continue the conversation...",
  "session.composer.aria": "Send to coordinator",
  "session.composer.send": "Send",
  "session.composer.startingToSend": "Starting the session. Your message will be sent automatically.",
  "session.composer.resumedAndSent": "Session resumed and message sent.",
  "session.composer.imageAttachment": "Image attachment",
  "session.composer.addAttachment": "Add image",
  "session.composer.removeAttachment": "Remove image",
  "session.composer.attachmentsUnsupported": "This Agent does not support image attachments.",
  "session.composer.attachmentsLimit": "You can attach up to four images per message.",
  "session.composer.attachmentsTypeUnsupported": "Only PNG, JPEG, and WebP images are supported.",
  "session.composer.attachmentsTooLarge": "This image is larger than the allowed 10 MB limit.",
  "session.question.singleAnswer": ({ question, answer }) => `Regarding “${question}”, my answer is: ${answer}`,
  "session.question.multiIntro": "Here are my answers:",
  "session.question.answerLabel": "Answer",
  "session.question.answerLine": ({ answer }) => `Answer: ${answer}`,
  "session.question.answeredCount": ({ count }) => `${count} questions answered`,
  "session.question.answered": "Answered",
  "session.question.syncing": "Answer sent. Updating the record...",
  "session.question.saved": "The question and answer remain in this session",
  "session.question.label": "Question",
  "session.question.groupTitle": "Please review these questions",
  "session.question.title": "Needs your input",
  "session.question.groupProgress": ({ current, total }) => `${current} of ${total} · Answers are sent together`,
  "session.question.singleDetail": "Your answer returns to this coordinator session",
  "session.question.recommendation": "Suggestion",
  "session.question.other": "Something else",
  "session.question.placeholder": "Share your answer...",
  "session.question.readonly": "Answer these in the coordinator session",
  "session.question.previous": "Previous",
  "session.question.next": "Next",
  "session.question.submitAll": "Submit answers",
  "session.question.submit": "Send answer",
  "session.prompt.title": "Confirmation required",
  "session.prompt.detail": "The provider is waiting for a choice",
  "session.prompt.readonly": "Worker session is read only. Respond in the coordinator session.",
  "session.prompt.hint": "Your choice goes directly to the native session and is not rewritten as a chat message.",
  "session.prompt.openTerminal": "Open native terminal",
  "session.tools.expand": "Expand session tools",
  "session.tools.collapse": "Collapse session tools",
  "session.list.show": "Show session list",
  "session.list.hide": "Hide session list",
  "session.work.createTitle": "Create an Orni workbench",
  "session.work.createDetail": "Orni Work maintains goals, plans, context, and progress from this conversation while keeping the native terminal available.",
  "session.work.createAction": "Start managing this work",
  "session.work.upgradeTitle": "Upgrade this session to Orni Work",
  "session.work.upgradeDetail": "Keep the native session and provider history, then maintain work documents, rounds, progress, and changes in the same session.",
  "session.work.upgradePending": "Connecting coordinator...",
  "session.work.upgradeAction": "Choose sources and upgrade",
  "session.upgrade.failed": "This session could not be upgraded.",
  "app.time.justNow": "Just now",
  "app.operationFailed": "The operation could not be completed.",
  "app.session.creatingCoordinator": "Creating coordinator session",
  "app.session.creatingProject": "Creating project session",
  "app.session.detailLoadFailed": "Session details could not be loaded.",
  "app.session.coordinatorReady": "The coordinator is ready and reviewing your goal.",
  "app.session.createFailed": "The session could not be created.",
  "session.starting.createErrorTitle": "Session creation failed",
  "session.starting.createErrorDetail": "The request could not be completed. Your input remains here so you can return and try again.",
  "app.session.importReading": "Session opened. Reading the information you selected...",
  "app.session.preparingProvider": "Session created. Preparing the provider...",
  "app.session.opening": "Opening session",
  "app.session.openingDetail": "Loading the native terminal and session history...",
  "app.session.openFailed": "This session could not be opened",
  "app.session.backToWorkbench": "Back to workbench",
  "app.notice.recoverySaved": "Failure recovery settings saved",
  "app.notice.sessionRenamed": "Session name updated",
  "app.notice.sessionStarted": "Session started",
  "app.notice.sessionStopped": "Session stopped",
  "app.notice.allSessionsStopped": "All sessions stopped",
  "app.notice.sessionRestarted": "Session restarted",
  "app.notice.sessionDeleted": "Session deleted",
  "app.notice.sessionRootSaved": "Session root saved",
  "app.historyImport.accepted": ({ count, provider }) => `Importing ${count} ${provider} session${Number(count) === 1 ? "" : "s"} in the background…`,
  "app.historyImport.completed": ({ count, provider }) => `${count} ${provider} session${Number(count) === 1 ? "" : "s"} imported`,
  "app.historyImport.failed": ({ count, provider }) => `Could not import ${count} ${provider} session${Number(count) === 1 ? "" : "s"}`,
  "create.history.providerScoped": "Only this Agent's history is queried",
  "runtime.error.workspaceRequired": "Choose a valid workspace.",
  "runtime.error.noWorkToRefresh": "This session does not have Orni Work information to refresh.",
  "runtime.error.upgradeFailed": "Orni Work was not enabled for this session.",
  "runtime.error.noWorkToTransfer": "This coordinator does not have an Orni Work to transfer.",
  "runtime.error.cloudDeviceRequired": "Sign in and connect this computer before creating Orni Work.",
  "runtime.error.coordinatorBindingChanged": "The coordinator changed on another device. Refresh before replacing it.",
  "runtime.error.sessionNameRequired": "Session name cannot be empty.",
  "runtime.error.attentionUnavailable": "This item no longer exists or was handled from another client.",
  "runtime.session.connecting": ({ provider }) => `Connecting to ${provider}`,
  "runtime.session.newCoordinatorTitle": ({ title }) => `${title} · New coordinator`,
  "runtime.session.fallbackTitle": ({ provider, id }) => `${provider} session ${id}`,
  "runtime.session.providerSession": ({ provider }) => `${provider} session`,
  "runtime.session.workerActive": ({ provider }) => `${provider} session working`,
  "runtime.session.workerUpdated": ({ provider }) => `${provider} session updated`,
  "runtime.attention.confirmDelivery": "Accept delivery",
  "runtime.attention.confirmContinue": "Continue",
  "runtime.attention.requestChanges": "Request changes",
  "runtime.round.notStarted": "Not started",
  "runtime.round.awaitingReview": "Agent reported completion; awaiting review",
  "runtime.owner.currentRun": "Current run",
  "runtime.stage.execution": "Execution",
  "runtime.capability.structuredProgress": "Structured progress",
  "runtime.capability.recovery": "Failure recovery",
  "runtime.impact.materials": ({ count }) => `${count} document${Number(count) === 1 ? "" : "s"}`,
  "runtime.impact.entities": ({ count }) => `${count} information item${Number(count) === 1 ? "" : "s"}`,
  "runtime.impact.rounds": ({ count }) => `${count} later round${Number(count) === 1 ? "" : "s"}`,
  "runtime.impact.assignments": ({ count }) => `${count} assignment${Number(count) === 1 ? "" : "s"}`,
  "runtime.impact.sessions": ({ count }) => `${count} session${Number(count) === 1 ? "" : "s"}`,
  "runtime.impact.operations": ({ count }) => `${count} operation${Number(count) === 1 ? "" : "s"}`,
  "runtime.listSeparator": ", ",
  "runtime.document.entityCount": ({ count, kind }) => `${count} ${kind} item${Number(count) === 1 ? "" : "s"}`,
  "runtime.entity.goal": "Goal",
  "runtime.entity.requirement": "Requirement",
  "runtime.entity.constraint": "Constraint",
  "runtime.entity.deliverable": "Deliverable",
  "runtime.entity.acceptance": "Acceptance criterion",
  "runtime.entity.architecture": "Architecture",
  "runtime.entity.design": "Design",
  "runtime.entity.audience": "Audience",
  "runtime.entity.scenario": "Scenario",
  "runtime.entity.risk": "Risk",
  "runtime.entity.decision": "Decision",
  "runtime.entity.unknown": "Open question",
  "runtime.material.strategy": "Strategy",
  "runtime.material.overview": "Overview",
  "runtime.material.title.workOverview": "Work overview",
  "runtime.material.title.workStrategy": "Work strategy",
  "runtime.material.technical": "Technical",
  "runtime.material.design": "Design",
  "runtime.material.standards": "Standards",
  "runtime.material.delivery": "Delivery",
  "runtime.material.acceptance": "Acceptance",
  "runtime.material.other": "Document",
  "runtime.activity.toolsWithFiles": ({ tools, files }) => `${tools} tool activities, including ${files} file changes`,
  "runtime.activity.tools": ({ count }) => `${count} tool activit${Number(count) === 1 ? "y" : "ies"}`,
  "runtime.activity.agentRunning": ({ detail }) => `Agent working · ${detail}`,
  "runtime.activity.starting": "Starting provider",
  "runtime.activity.stopping": "Stopping provider",
  "runtime.activity.stopped": "Stopped; ready to restart",
  "runtime.activity.exited": "Provider exited",
  "runtime.activity.error": "Provider failed to start or encountered an error",
  "runtime.activity.terminalRunning": "Native terminal running",
  "runtime.report.startedStage": ({ stage }) => `Started: ${stage}`,
  "runtime.report.started": "Worker started",
  "runtime.report.progressStage": ({ stage }) => `Progress: ${stage}`,
  "runtime.report.progress": "Worker reported progress",
  "runtime.report.resultStage": ({ stage }) => `Result: ${stage}`,
  "runtime.report.result": "Worker reported a partial result",
  "runtime.report.waitingInput": "Worker needs more information",
  "runtime.report.waitingApproval": "Worker is waiting for confirmation",
  "runtime.report.waitingDependency": "Worker is waiting on a dependency",
  "runtime.report.problem": "Worker encountered a problem",
  "runtime.report.completed": "Worker submitted the final result",
  "runtime.report.cancelled": "Worker work was cancelled",
  "runtime.report.updateStage": ({ stage }) => `Update: ${stage}`,
  "runtime.report.update": "Worker updated progress",
  "runtime.report.step": ({ current, total }) => `Step ${current}/${total}`,
  "runtime.report.completedItems": ({ items }) => `Completed: ${items}`,
  "runtime.report.next": ({ action }) => `Next: ${action}`,
  "runtime.report.question": ({ question }) => `Needs confirmation: ${question}`,
  "runtime.report.statusUpdate": "Worker submitted a status update.",
  "runtime.sessionLeg.preparing": "Preparing",
  "runtime.sessionLeg.active": "Working",
  "runtime.sessionLeg.completed": "Completed",
  "runtime.sessionLeg.failed": "Failed",
  "runtime.sessionLeg.replaced": "Handed off",
  "runtime.sessionLeg.detached": "Detached",
  "runtime.sessionLeg.unknown": "Status unknown",
  "runtime.assignment.proposed": "Awaiting acceptance",
  "runtime.assignment.active": "In progress",
  "runtime.assignment.paused": "Paused",
  "runtime.assignment.completed": "Completed",
  "runtime.assignment.replaced": "Reassigned",
  "runtime.assignment.revoked": "Revoked",
  "runtime.daemon.offline": "Local Daemon unavailable",
  "runtime.native.starting": "Starting local service",
  "runtime.native.startingDetail": "Orni is connecting to the local Daemon…",
  "runtime.native.offline": "Local service unavailable",
  "runtime.native.offlineDetail": "The Desktop shell is open, but the local Daemon did not respond.",
  "runtime.native.retry": "Try again",
  "workbench.aria": "Workbench navigation",
  "workbench.pageAria": "Workbench page",
  "workbench.nav.work": "Work",
  "workbench.nav.overview": "Overview",
  "workbench.nav.rounds": "Progress",
  "workbench.nav.sessions": "Sessions",
  "workbench.nav.confirmations": "Confirmations",
  "workbench.nav.mutations": "Change history",
  "workbench.nav.documents": "Work documents",
  "workbench.nav.settings": "Settings",
  "workbench.nav.recovery": "Failure recovery",
  "workbench.nav.organize": "Refresh context",
  "workbench.documents.empty": "The coordinator creates documents when the work needs them.",
  "workbench.overview.revision": ({ revision }) => `Work overview · rev ${revision}`,
  "workbench.overview.workerLatest": "Latest worker update",
  "workbench.overview.currentFocus": "Current focus",
  "workbench.overview.noRound": "No work round planned yet",
  "workbench.overview.openRound": "Open round",
  "workbench.overview.recentRecords": "Recent work updates",
  "workbench.overview.allRecords": "View all updates",
  "workbench.scope.all": "Whole project",
  "workbench.overview.currentRound": "Current round",
  "workbench.overview.awaitingPlan": "Awaiting plan",
  "workbench.overview.allRounds": "View every work round",
  "workbench.overview.workSessions": "Work sessions",
  "workbench.overview.activeSessions": ({ count }) => `${count} active session${Number(count) === 1 ? "" : "s"}`,
  "workbench.overview.noActiveSessions": "No active sessions",
  "workbench.overview.sessionHistory": "View current and past sessions",
  "workbench.overview.confirmations": "Confirmations",
  "workbench.overview.pendingCount": ({ count }) => `${count} pending item${Number(count) === 1 ? "" : "s"}`,
  "workbench.overview.noPending": "Nothing needs confirmation",
  "workbench.overview.confirmationHistory": "View confirmation history",
  "workbench.overview.recentChanges": "Recent changes",
  "workbench.overview.noChanges": "No changes yet",
  "workbench.overview.allChanges": "View the full information history",
  "workbench.overview.roundsTitle": "Work rounds",
  "workbench.overview.roundsLead": "See what is happening now and what each planned round is meant to deliver.",
  "workbench.overview.viewAll": "View all",
  "workbench.overview.roundsEmpty": "No work rounds yet. Keep talking with the coordinator and the plan will appear here.",
  "workbench.rounds.eyebrow": "Progress",
  "workbench.rounds.title": "Follow the work one round at a time",
  "workbench.rounds.lead": "Rounds are flexible execution containers. The coordinator decides whether new ideas and changes belong in the current round, a later round, or a new one.",
  "workbench.rounds.latest": ({ detail }) => `Latest: ${detail}`,
  "workbench.rounds.currentVersion": "Current version",
  "workbench.rounds.awaitingStart": "Waiting for this round to begin",
  "workbench.rounds.completion": "Completion criteria",
  "workbench.rounds.criteriaCount": ({ count }) => `${count} criteri${Number(count) === 1 ? "on" : "a"}`,
  "workbench.rounds.criteriaManaged": "Maintained by the coordinator as the work evolves",
  "workbench.rounds.workerLatest": "Latest worker activity",
  "workbench.rounds.workSessions": "Work sessions",
  "workbench.rounds.noWorkerSessions": "No worker sessions linked to this round",
  "workbench.rounds.updateCount": ({ count }) => `${count} work update${Number(count) === 1 ? "" : "s"}`,
  "workbench.rounds.openDetail": "Open round details",
  "workbench.rounds.emptyTitle": "No work rounds yet",
  "workbench.rounds.emptyDetail": "The coordinator creates rounds dynamically from the work goal and your confirmations.",
  "workbench.roundDetail.back": "Back to work rounds",
  "workbench.roundDetail.switch": "Switch round",
  "workbench.roundDetail.overview": "Overview",
  "workbench.roundDetail.sessions": "Sessions",
  "workbench.roundDetail.notes": "Notes",
  "workbench.roundStatus.active": "In progress",
  "workbench.roundStatus.review": "Awaiting review",
  "workbench.roundStatus.completed": "Completed",
  "workbench.roundStatus.paused": "Paused",
  "workbench.roundStatus.needsReview": "Needs review",
  "workbench.roundStatus.planned": "Ready",
  "workbench.overview.organizing": "The coordinator is organizing the latest project information.",
  "workbench.sessions.eyebrow": "Session management",
  "workbench.sessions.title": "Coordinator and worker sessions",
  "workbench.sessions.lead": "Check every session at a glance, or follow the Worker Master activity across rounds.",
  "workbench.sessions.pauseAll": "Pause all",
  "workbench.sessions.all": "All sessions",
  "workbench.sessions.activity": "Detailed activity",
  "workbench.sessions.coordinator": "Coordinator",
  "workbench.sessions.authority": "Workbench authority",
  "workbench.sessions.replaceCoordinator": "Replace coordinator",
  "workbench.sessions.pause": "Pause",
  "workbench.sessions.replace": "Replace",
  "workbench.sessions.readOnly": "View read only",
  "workbench.sessions.emptyTitle": "No linked sessions",
  "workbench.sessions.emptyDetail": "Current and past sessions will appear here after the coordinator assigns work.",
  "workbench.sessionHistory.active": "In progress",
  "workbench.sessionHistory.waiting": "Waiting",
  "workbench.sessionHistory.completed": "Completed",
  "workbench.sessionHistory.failed": "Failed",
  "workbench.sessionHistory.replaced": "Replaced",
  "workbench.activity.dispatch": "Assignment",
  "workbench.activity.review": "Review",
  "workbench.activity.delivery": "Result",
  "workbench.activity.workerReport": "Worker report",
  "workbench.activity.provider": "Provider activity",
  "workbench.activity.latest": "Latest",
  "workbench.activity.emptyTitle": "No work activity yet",
  "workbench.activity.emptyDetail": "Worker starts, progress reports, blockers, reviews, and results will appear here in time order.",
  "workbench.sources.eyebrow": "Refresh context",
  "workbench.sources.title": "Bring the coordinator up to date",
  "workbench.sources.lead": "Choose the sources it may reference. Reading, reconciliation, and follow-up questions continue in the existing coordinator session.",
  "workbench.sources.projectSources": "Project sources",
  "workbench.sources.documents": "Documents",
  "workbench.sources.code": "Code",
  "workbench.sources.other": "Other files",
  "workbench.sources.history": "Additional session history",
  "workbench.sources.historyLead": "Optional. Only complete sessions selected here will be referenced.",
  "workbench.sources.refresh": "Refresh",
  "workbench.sources.userMessages": ({ count }) => `${count} user message${Number(count) === 1 ? "" : "s"}`,
  "workbench.sources.readingHistory": "Reading provider history for this workspace...",
  "workbench.sources.noHistory": "No additional sessions found.",
  "workbench.sources.request": "Anything to pay special attention to? (optional)",
  "workbench.sources.requestPlaceholder": "For example: compare the current code with the requirements and technical docs.",
  "workbench.sources.contextIncluded": "The current coordinator session and workbench are always included",
  "workbench.sources.submitting": "Submitting...",
  "workbench.sources.submit": "Send to coordinator",
  "workbench.sources.historyError": "No usable session history was found.",
  "workbench.sources.submitError": "The context refresh request could not be submitted.",
  "workbench.pause.aria": "Pause session",
  "workbench.pause.error": "The session could not be paused.",
  "workbench.pause.titleAll": ({ count }) => `Pause ${count} active sessions`,
  "workbench.pause.titleOne": ({ title }) => `Pause “${title}”`,
  "workbench.pause.lead": "Interrupt the current operation, or stop the provider session entirely.",
  "workbench.pause.interrupt": "Send Esc",
  "workbench.pause.interruptAll": "Send Esc to all",
  "workbench.pause.interruptLead": "Interrupt the current operation while keeping the session available.",
  "workbench.pause.exit": "Exit session",
  "workbench.pause.exitAll": "Exit all sessions",
  "workbench.pause.exitLead": "Stop the provider process. You can restart and resume it later.",
  "workbench.handoff.coordinatorTitle": "Replace coordinator",
  "workbench.handoff.coordinatorLead": "The workbench, rounds, questions, decisions, and worker links stay intact. The previous coordinator remains available as read-only history.",
  "workbench.handoff.coordinatorError": "The new coordinator did not complete the handoff.",
  "workbench.handoff.workerTitle": "Replace worker",
  "workbench.handoff.workerLead": "The new worker receives the authoritative documents, prior reports, and the same assignment. The previous session remains available as read-only history.",
  "workbench.handoff.workerError": "The new worker did not complete the handoff.",
  "workbench.handoff.workerSessionTitle": ({ title }) => `${title} · Handoff`,
  "workbench.handoff.newProvider": "New provider",
  "workbench.handoff.note": "Handoff note (optional)",
  "workbench.handoff.notePlaceholder": "For example: continue from the current state, but verify the technical docs first.",
  "workbench.handoff.reason": "Reason for replacement (optional)",
  "workbench.handoff.reasonPlaceholder": "For example: the previous session exited unexpectedly; continue from its completed work.",
  "workbench.handoff.pending": "Handing off...",
  "workbench.handoff.confirm": "Confirm replacement",
  "workbench.roundNotes.current": "Current round version",
  "workbench.roundNotes.changes": "Assignments and changes",
  "workbench.confirmations.eyebrow": "Confirmations",
  "workbench.confirmations.title": "Everything that needs your decision",
  "workbench.confirmations.lead": "Answers return to the coordinator as normal messages. Later and Ignore only update the inbox state.",
  "workbench.confirmations.batch": "Batch",
  "workbench.confirmations.single": "One by one",
  "workbench.confirmations.pending": "Pending",
  "workbench.confirmations.history": "History",
  "workbench.confirmations.runtimeAlert": "Runtime alert",
  "workbench.confirmations.deferred": "Later",
  "workbench.confirmations.notePlaceholder": "Add context or enter another answer...",
  "workbench.confirmations.answerPlaceholder": "Enter your answer...",
  "workbench.confirmations.later": "Later",
  "workbench.confirmations.reopen": "Return to pending",
  "workbench.confirmations.ignore": "Ignore",
  "workbench.confirmations.send": "Send answer",
  "workbench.confirmations.coordinator": "Coordinator",
  "workbench.confirmations.previous": "Previous",
  "workbench.confirmations.next": "Next",
  "workbench.confirmations.answeredCount": ({ answered, total }) => `${answered} of ${total} answered`,
  "workbench.confirmations.sending": "Sending...",
  "workbench.confirmations.submit": "Submit",
  "workbench.confirmations.emptyTitle": "Nothing needs confirmation",
  "workbench.confirmations.emptyDetail": "New questions, worker requests, runtime alerts, and reviews will appear here.",
  "workbench.confirmations.completed": "Completed",
  "workbench.confirmations.ignored": "Ignored",
  "workbench.confirmations.historyEmptyTitle": "No confirmation history",
  "workbench.confirmations.historyEmptyDetail": "Completed and ignored items retain their full context here.",
  "workbench.confirmations.answerSeparator": "; ",
  "workbench.confirmations.singleAnswer": ({ prompt, answer }) => `For “${prompt}”, my answer is: ${answer}`,
  "workbench.confirmations.batchAnswer": ({ answers }) => `Here are my answers to the pending items:\n${answers}`,
  "workbench.confirmations.batchAnswerItem": ({ index, prompt, answer }) => `${index}. ${prompt}\nAnswer: ${answer}`,
  "workbench.confirmations.sendError": "The answer could not be sent.",
  "workbench.confirmations.sendRetry": "The answer could not be sent. Try again.",
  "workbench.confirmations.statusError": "The item status could not be updated.",
  "workbench.documents.viewAria": "Document view",
  "workbench.documents.openChanges": "View changes",
  "workbench.documents.view.markdown": "Document",
  "workbench.documents.view.overview": "Overview",
  "workbench.documents.view.table": "Table",
  "workbench.documents.view.tree": "Structure",
  "workbench.documents.view.map": "Relationships",
  "workbench.documents.view.timeline": "Timeline",
  "workbench.documents.view.board": "Board",
  "workbench.documents.view.slides": "Slides",
  "workbench.mutations.eyebrow": "Change history",
  "workbench.mutations.title": "See what changed and why",
  "workbench.mutations.lead": "Batch view reconstructs everything affected by one interaction. Information view lists each field change separately.",
  "workbench.mutations.clearFilter": "Clear information filter",
  "workbench.mutations.batches": "By interaction",
  "workbench.mutations.operations": "All information changes",
  "workbench.mutations.search": "Filter by information ID",
  "workbench.mutations.loading": "Loading change history...",
  "workbench.mutations.retry": "Try again",
  "workbench.mutations.emptyTitle": "No information changes yet",
  "workbench.mutations.noMatchTitle": "No matching changes",
  "workbench.mutations.emptyDetail": "Internal revision and timestamp-only updates are hidden.",
  "workbench.mutations.loadEarlier": "Load earlier changes",
  "workbench.mutations.loadError": "Change history could not be loaded.",
  "workbench.mutations.loadMoreError": "Earlier changes could not be loaded.",
  "workbench.mutations.changeCount": ({ count }) => `${count} information change${Number(count) === 1 ? "" : "s"}`,
  "workbench.mutations.confirmed": "Linked to user confirmation",
  "workbench.mutations.trigger": "Trigger and confirmation",
  "workbench.mutations.system": "System maintenance",
  "workbench.mutations.confirmationPrefix": "Confirmation: ",
  "workbench.mutations.raw": "View complete raw record",
  "workbench.mutations.before": "Before",
  "workbench.mutations.after": "After",
  "workbench.mutations.unset": "Not set",
  "workbench.mutations.emptyValue": "Empty",
  "workbench.mutations.emptyString": "Empty string",
  "workbench.mutations.none": "None",
  "api.title": "API Manager",
  "api.lead": "Configure model providers for Codex, OpenCode, and Claude Code from one place.",
  "api.tab.config": "Config",
  "api.tab.monitor": "Monitor",
  "api.loading": "Connecting to the API service",
  "api.loadingDetail": "The independent Orni API Daemon keeps running when this window closes.",
  "api.error.offline": "The API service is not running. Start Orni's local services and try again.",
  "api.error.action": "The API setting could not be updated.",
  "api.providers.title": "Providers",
  "api.providers.count": ({ count }) => `${count} configured`,
  "api.providers.empty": "No matching provider",
  "api.providers.emptyDetail": "Add an API source or clear the filters.",
  "api.filter.protocolAll": "All protocols",
  "api.filter.cliAll": "All CLIs",
  "api.provider.add": "Add provider",
  "api.provider.update": "Update provider",
  "api.provider.edit": "Edit provider",
  "api.provider.editorLead": "Name, endpoint, and protocol are required except for Bedrock. Credentials, model, website, and notes are optional.",
  "api.field.required": "Required",
  "api.provider.name": "Name",
  "api.provider.protocol": "Protocol",
  "api.provider.detectProtocol": "Detect protocol",
  "api.provider.protocolDetecting": "Checking the endpoint protocol...",
  "api.provider.protocolDetected": ({ protocol }) => `${protocol} detected. Other fields were left unchanged.`,
  "api.provider.protocolDetectFailed": "The endpoint protocol could not be identified.",
  "api.provider.url": "Base URL",
  "api.provider.defaultModel": "Default model",
  "api.provider.clearModel": "Clear default model",
  "api.provider.modelPlaceholder": "Enter a model ID",
  "api.provider.homepage": "Website",
  "api.provider.notes": "Notes",
  "api.provider.compatibility": "Available CLIs",
  "api.provider.unsupported": "Not supported",
  "api.provider.modelUnset": "Model not set",
  "api.provider.keyStored": "A credential is already stored. Leave blank to keep it.",
  "api.provider.keyReplace": "Enter a new key to replace the stored credential",
  "api.provider.keyPlaceholder": "Stored only in the local API database",
  "api.provider.test": "Test",
  "api.provider.testEndpoint": "Test request",
  "api.provider.testModel": "Test model",
  "api.provider.testSuccess": "Available",
  "api.provider.testError": "Failed",
  "api.provider.noStatus": "No HTTP status",
  "api.provider.testing": "Testing the provider...",
  "api.provider.testComplete": "The provider test completed.",
  "api.provider.testFailed": "The provider test failed.",
  "api.provider.models": "Get models",
  "api.provider.modelsLoading": "Loading model names...",
  "api.provider.modelsLoaded": ({ count }) => `${count} models loaded.`,
  "api.provider.modelsAvailable": "Available models",
  "api.provider.modelsEmpty": "No models were returned by this endpoint.",
  "api.provider.modelsFailed": "Model discovery failed.",
  "api.provider.deleteTitle": "Delete this provider?",
  "api.provider.deleteDetail": ({ name }) => `${name} will also be removed from every CLI routing list.`,
  "api.proxy.title": "Local API proxy",
  "api.proxy.runningDetail": "Failover, health recovery, and request monitoring are active in the background.",
  "api.proxy.stoppedDetail": "Direct mode can still work, but automatic failover and request monitoring are unavailable.",
  "api.proxy.port": "Port",
  "api.proxy.start": "Start proxy",
  "api.proxy.stop": "Stop proxy",
  "api.proxy.online": "Running",
  "api.proxy.offline": "Stopped",
  "api.proxy.keyUnavailable": "Restart the API service to load the proxy key",
  "api.proxy.disableTitle": "Stop the local proxy?",
  "api.proxy.disableDetail": "CLI configurations that use the proxy will stop working until it is started again.",
  "api.proxy.route.responses": "Responses endpoint",
  "api.proxy.route.chat": "Chat endpoint",
  "api.proxy.route.anthropic": "Anthropic endpoint",
  "api.cli.codex.support": "Codex custom providers use the OpenAI Responses wire API; Bedrock uses native AWS credentials.",
  "api.cli.opencode.support": "OpenCode supports OpenAI-compatible and Anthropic-compatible providers.",
  "api.cli.claude.support": "Claude Code uses Anthropic Messages or its native Bedrock integration.",
  "api.cli.endpoint": "Endpoint",
  "api.cli.directEndpoint": "The selected provider URL is written directly to the CLI configuration.",
  "api.mode.proxy": "Use proxy",
  "api.mode.direct": "Direct",
  "api.mode.proxyTitle": "Recommended",
  "api.mode.proxyDetail": "Requests use the first healthy enabled provider and fail over automatically.",
  "api.mode.proxyOfflineTitle": "Local proxy is off",
  "api.mode.proxyOfflineDetail": "This CLI is set to use the proxy. If its current configuration points to the proxy, requests may fail until the proxy is started.",
  "api.mode.directTitle": "Direct connection",
  "api.mode.directDetail": "Only the first enabled provider is used. Other entries are inactive and failures cannot switch providers.",
  "api.direct.confirmTitle": "Switch to direct mode?",
  "api.direct.confirmDetail": "Automatic failover and proxy request monitoring will no longer protect this CLI.",
  "api.routing.title": "Provider order",
  "api.routing.proxyDetail": "Drag to set priority. Unhealthy providers are skipped until they recover.",
  "api.routing.directDetail": "Only the first enabled provider is active in direct mode.",
  "api.routing.add": "Add provider",
  "api.routing.refresh": "Refresh",
  "api.routing.recover": "Test and restore route",
  "api.routing.remove": "Remove from route",
  "api.routing.endpointReachable": "Request succeeded",
  "api.routing.endpointFailed": "Request failed",
  "api.routing.endpointScope": "Real model request; output capped at 16 tokens.",
  "api.routing.pickerDetail": "Choose a compatible provider. It is added and takes effect immediately.",
  "api.routing.noneAvailable": "No other compatible providers are available.",
  "api.routing.empty": "No provider selected",
  "api.routing.emptyDetail": "Add at least one compatible provider before updating the CLI configuration.",
  "api.routing.standbyDisabled": "Inactive in direct mode",
  "api.routing.enable": "Enable provider",
  "api.routing.disable": "Disable provider",
  "api.routing.moveUp": "Move up",
  "api.routing.moveDown": "Move down",
  "api.current.view": "View current config",
  "api.current.refresh": "Refresh current config",
  "api.current.readFailed": "The current CLI configuration could not be read.",
  "api.current.label": "Current CLI configuration",
  "api.current.proxyTitle": "Already using the Orni proxy",
  "api.current.proxyBadge": "Using proxy",
  "api.current.matched": "Matches a saved provider",
  "api.current.add": "Add to providers",
  "api.current.addDetail": "Adding saves this endpoint and its existing credential in Orni's local API database and places it first when compatible. The CLI file is not changed.",
  "api.current.emptyTitle": "No current API configuration",
  "api.current.emptyDetail": "This CLI does not currently expose a supported API configuration.",
  "api.current.opencodeModelUnsetTitle": "No model selected in OpenCode config",
  "api.current.opencodeModelUnsetDetail": "The file is valid. OpenCode can use its recent model or let you choose one from its model picker.",
  "api.current.unreadableTitle": "Current config could not be read",
  "api.current.notConfigured": "Not configured",
  "api.current.unreadable": "Unreadable",
  "api.config.preview": "Review & Apply",
  "api.config.previewTitle": ({ cli }) => `Review ${cli} configuration`,
  "api.config.before": "Current file",
  "api.config.after": "Proposed file",
  "api.config.empty": "This file does not exist yet.",
  "api.config.remoteRedacted": "File contents are redacted on remote clients; review remains on the target computer before applying.",
  "api.config.apply": "Apply configuration",
  "api.warning.proxy_recommended": "Proxy mode keeps credentials and failover under Orni's control",
  "api.warning.direct_mode_no_failover": "Direct mode has no automatic failover",
  "api.warning.direct_mode_writes_secret": "The upstream credential will be written to the CLI configuration",
  "api.warning.native_credential_chain": "This CLI will use the platform AWS credential chain",
  "api.confirm.continue": "Continue",
  "api.status.available": "Available",
  "api.status.degraded": "Degraded",
  "api.status.unavailable": "Unavailable",
  "api.status.unknown": "Not tested",
  "api.monitor.title": "Recent requests",
  "api.monitor.retention": "Only request metadata from the last 15 minutes is retained. Bodies and credentials are never logged.",
  "api.monitor.providerAll": "All providers",
  "api.monitor.refresh": "Refresh",
  "api.monitor.retrying": "Retrying",
  "api.monitor.time": "Time",
  "api.monitor.client": "Client / User-Agent",
  "api.monitor.providerModel": "Provider / Model",
  "api.monitor.request": "Request",
  "api.monitor.size": "Size",
  "api.monitor.status": "Status",
  "api.monitor.latency": "Latency",
  "api.monitor.unknownProvider": "Unknown provider",
  "api.monitor.empty": "No recent requests",
  "api.monitor.emptyDetail": "Requests routed through the local proxy will appear here.",
  "notes.title": "Idea & Note",
  "notes.new": "New notebook",
  "notes.inbox": "Quick notes",
  "notes.untitled": "Untitled note",
  "notes.notebook": "Notebook",
  "notes.empty": "Nothing here yet. Start with a note above.",
  "notes.emptyShort": "Empty",
  "notes.noSelection": "Choose a notebook or create one.",
  "notes.blockCount": ({ count }) => `${count} block${Number(count) === 1 ? "" : "s"}`,
  "notes.capturePlaceholder": "Write something...",
  "notes.quickCapture": "Quick Capture",
  "notes.associateSession": "Associate with a session",
  "notes.associateSessionDetail": "Choose where this notebook belongs. Sending is available after it is associated.",
  "notes.createAndAssociate": "Create and associate a session",
  "notes.createAndAssociateDetail": "Configure a new session, then associate this notebook automatically.",
  "notes.existingSessions": "Existing sessions",
  "notes.noSessionsAvailable": "No sessions are available yet.",
  "notes.removeAssociation": "Remove association",
  "notes.openSession": "Open session",
  "notes.sendNotebook": "Send notebook",
  "notes.sendBlock": "Send block to session",
  "notes.confirmOpenTitle": "Open this session?",
  "notes.confirmOpenDetail": "You will leave Idea & Note and open the associated session.",
  "notes.confirmSendNoteTitle": "Send these notes?",
  "notes.confirmSendBlockTitle": "Send these blocks?",
  "notes.confirmSendDetail": "Review the selected content before sending it to the associated session.",
  "notes.destinationSession": "Destination session",
  "notes.selectNotebooks": "Notes to send",
  "notes.selectBlocks": "Blocks to send",
  "notes.currentSelection": "Current selection · required",
  "notes.included": "Included",
  "notes.notIncluded": "Not included",
  "notes.confirmOpen": "Open session",
  "notes.confirmSend": "Send and open",
  "notes.linkBeforeSending": "Associate this notebook with a session first",
  "notes.associationFailed": "The session association could not be updated.",
  "notes.sendFailed": "The note could not be sent to the associated session.",
  "notes.noSession": "No session",
  "notes.noProject": "No project",
  "notes.insertIntoSession": "Insert into session input",
  "notes.move": "Move block",
  "notes.copy": "Copy block",
  "notes.copied": "Copied",
  "notes.reorder": "Reorder block",
  "notes.deleteBlock": "Delete block",
  "notes.deleteBlockConfirm": "Delete this block?",
  "notes.merge": "Merge block",
  "notes.mergeNotebook": "Merge notebook",
  "notes.mergedFrom": ({ source }) => `Merged from ${source}`,
  "notes.targetBlock": "Merge into",
  "notes.noMergeTarget": "This notebook has no other block to merge into.",
  "notes.confirmAction": "Confirm",
  "notes.action.move": "Move block",
  "notes.action.merge": "Merge block",
  "notes.action.merge_note": "Merge notebook",
  "notes.action.chooseTarget": "Choose the destination here. This takes effect in one step.",
  "notes.deleteTitle": "Delete this notebook?",
  "notes.deleteDetail": "All blocks in this notebook will be permanently deleted.",
  "notes.loadFailed": "Notes could not be loaded.",
  "notes.saveFailed": "The note could not be saved.",
  "notes.actionFailed": "The note could not be changed.",
  "notes.cloudRequired": "Sign in to Orni Cloud to view or save Notes. Unsaved drafts remain on this device.",
} satisfies Record<string, TranslationValue>;

export type TranslationKey = keyof typeof en;
